/**
 * ExternalDappIframe Component
 *
 * Renders an external minting dapp in an iframe with postMessage communication.
 * Handles URL validation, iframe loading states, and wallet state synchronization.
 *
 * @module minting/components/ExternalDappIframe
 * @category Minting Framework
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { usePostMessageBridge } from '../hooks/usePostMessageBridge';
import { IframeLoader } from './IframeLoader';
import {
  validateUrl,
  sanitizeUrl,
  extractOrigin,
} from '../utils/urlValidator';
import type { WalletState } from '../types';
import { AppContext } from '../../App';
import { useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';

/**
 * Props for ExternalDappIframe
 */
export interface ExternalDappIframeProps {
  /** The minting dapp configuration */
  dapp: {
    id: string;
    name: string;
    url: string;
    config?: {
      allowedOrigins?: string[];
      sandbox?: string;
    };
  };
  /** Optional CSS class name */
  className?: string;
  /** Optional style overrides */
  style?: React.CSSProperties;
  /** Callback when iframe reports ready */
  onReady?: () => void;
  /** Callback for any error */
  onError?: (error: Error) => void;
}

/**
 * ExternalDappIframe - Embeds an external dapp via iframe with wallet bridge.
 *
 * Features:
 * - Validates URLs for security
 * - Automatically sends AppKit state to iframe
 * - Listens for wallet requests (connect, signTx, etc.)
 * - Handles iframe loading and error states
 *
 * @example
 * ```tsx
 * <ExternalDappIframe
 *   dapp={{
 *     id: 'external-mint',
 *     name: 'External Mint',
 *     url: 'https://external-dapp.com/mint',
 *     config: { allowedOrigins: ['https://external-dapp.com'] }
 *   }}
 * />
 * ```
 */
export const ExternalDappIframe: React.FC<ExternalDappIframeProps> = ({
  dapp,
  className = '',
  style = {},
  onReady,
  onError,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Mirrors isLoading for the watchdog interval, which must not re-arm the
  // effect just to read a boolean.
  const isLoadingRef = useRef(true);
  isLoadingRef.current = isLoading;
  const [loadError, setLoadError] = useState<string | null>(null);
  const [iframeOrigin, setIframeOrigin] = useState<string | null>(null);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const { appKit } = React.useContext(AppContext);

  // Get wallet state using Reown AppKit hooks (correct API)
  const { address, isConnected } = useAppKitAccount();
  const { chainId } = useAppKitNetwork();

  // Memoized: a fresh object literal here would be a new dependency on every
  // render, re-running the load-listener effect below each time.
  const currentWalletState: WalletState = useMemo(
    () => ({
      address: address ?? null,
      chainId: Number(chainId) || null,
      isConnected: isConnected || false,
    }),
    [address, chainId, isConnected],
  );

  // Validate and sanitize URL
  const urlValidation = useMemo(() => {
    const valid = validateUrl(dapp.url, dapp.config?.allowedOrigins);
    if (!valid) {
      return { valid: false, url: null as string | null, error: 'Invalid or disallowed URL' };
    }
    const cleanUrl = sanitizeUrl(dapp.url);
    if (!cleanUrl) {
      return { valid: false, url: null as string | null, error: 'URL sanitization failed' };
    }
    return { valid: true, url: cleanUrl as string | null, error: null as string | null };
  }, [dapp.url, dapp.config?.allowedOrigins]);

  // Set up postMessage bridge
  const { sendMessage, sendAppKitState } = usePostMessageBridge(iframeRef, {
    targetOrigin: iframeOrigin || '*', // Use specific origin if known
    onRequestConnect: async () => {
      if (!appKit) {
        throw new Error('AppKit not initialized');
      }
      // Connect using AppKit
      await appKit.connect();
    },
    onRequestSignTx: async (transaction, requestId) => {
      if (!appKit) {
        throw new Error('AppKit not initialized');
      }
      // Sign transaction using AppKit
      const provider = await appKit.getProvider();
      const signer = provider.getSigner();
      const result = await signer.sendTransaction(transaction);
      return result;
    },
    onRequestSignMessage: async (msg, requestId, address) => {
      if (!appKit) {
        throw new Error('AppKit not initialized');
      }
      // Sign message using AppKit
      const provider = await appKit.getProvider();
      const signer = provider.getSigner();

      if (typeof msg === 'string') {
        return await signer.signMessage(msg);
      } else {
        // EIP-712 typed data
        return await signer.signTypedData(msg.domain, msg.types, msg.message);
      }
    },
    onRequestSwitchChain: async (chainId, requestId) => {
      if (!appKit) {
        throw new Error('AppKit not initialized');
      }
      // Switch chain using AppKit
      await appKit.switchChain(chainId);
    },
    onError: (error, message) => {
      console.error('[ExternalDappIframe] Bridge error:', error, message);
      onError?.(error);
    },
    onChildReady: () => {
      console.log(`[ExternalDappIframe] Child dapp "${dapp.id}" reported ready`);
      setIsIframeReady(true);
      onReady?.();
    },
  });

  /**
   * Send current AppKit state to iframe
   */
  const sendWalletState = useCallback(() => {
    if (!appKit) {
      console.warn('[ExternalDappIframe] AppKit not available');
      return;
    }

    // Use the current wallet state from hooks
    sendAppKitState(currentWalletState);
  }, [sendAppKitState, appKit, currentWalletState]);

  /**
   * Set up iframe load/unload handlers and initial AppKit state push
   */
  useEffect(() => {
    if (!urlValidation.valid || !urlValidation.url) {
      setIsLoading(false);
      setLoadError(urlValidation.error || 'URL validation failed');
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      console.log(`[ExternalDappIframe] Loaded: ${dapp.name}`);
      setIsLoading(false);
      setLoadError(null);

      // Extract origin for secure messaging
      const origin = extractOrigin(urlValidation.url!);
      if (origin) {
        setIframeOrigin(origin);
      }

      // Send initial AppKit state after a brief delay to ensure iframe is ready
      setTimeout(() => {
        sendWalletState();
      }, 500);
    };

    const handleError = () => {
      console.error(`[ExternalDappIframe] Failed to load: ${dapp.name}`);
      setIsLoading(false);
      setLoadError('Failed to load the dapp. The URL may be incorrect or the server is unreachable.');
      onError?.(new Error('Iframe load error'));
    };

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    // Watchdog. The load listener is re-attached whenever this effect re-runs,
    // and a load event that lands in the gap between cleanup and re-attach is
    // lost for good, leaving the spinner up with no way back. We only treat
    // already-complete documents as loaded, so this can never hide a real hang:
    // a genuinely slow child stays on the spinner, a lost event does not.
    const watchdog = window.setInterval(() => {
      if (isLoadingRef.current) {
        try {
          if (iframe.contentDocument?.readyState === 'complete') {
            handleLoad();
          }
        } catch {
          // Cross-origin access denied before the child is ready: keep waiting.
        }
      }
    }, 400);

    return () => {
      window.clearInterval(watchdog);
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
    };
  }, [urlValidation, dapp.name, sendWalletState, onError]);

  /**
   * Subscribe to AppKit state changes and forward to iframe
   */
  useEffect(() => {
    if (!appKit) return;

    // Send initial state
    sendWalletState();

    // Note: We're using Reown AppKit hooks, so wallet state updates are reactive
    // No need to manually subscribe - the hooks will trigger re-renders

    // If we wanted to manually subscribe to appKit state changes, we could:
    // const unsubscribe = appKit.subscribeState((state) => { ... });
    // But using hooks is cleaner
  }, [sendWalletState, appKit]);

  /**
   * Listen for custom messages sent directly to window (fallback for iframe->parent)
   * The usePostMessageBridge hook already handles this, but we can add additional
   * custom message handlers here if needed.
   */
  useEffect(() => {
    const handleGlobalMessage = (event: MessageEvent) => {
      // Additional custom handling can go here if needed
    };

    window.addEventListener('message', handleGlobalMessage);
    return () => window.removeEventListener('message', handleGlobalMessage);
  }, []);

  // Render error state
  if (!urlValidation.valid || loadError) {
    return (
      <div
        className={`external-dapp-error ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          backgroundColor: '#1a1a1a',
          ...style,
        }}
      >
        <div className="text-center p-4">
          <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h5 className="text-light mb-2">Unable to Load Dapp</h5>
          <p className="text-light opacity-6 mb-0 small">
            {loadError || 'Invalid dapp configuration'}
          </p>
        </div>
      </div>
    );
  }

  // Render iframe with loading overlay
  return (
    <div
      className={`embedded-dapp-container ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: '400px',
        ...style,
      }}
    >
      <IframeLoader isLoading={isLoading} message={`Loading ${dapp.name}...`} />
      {urlValidation.url && (
        <iframe
          ref={iframeRef}
          src={urlValidation.url}
          title={dapp.name}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: isLoading ? 'none' : 'block',
          }}
          sandbox={
            dapp.config?.sandbox ||
            'allow-scripts allow-same-origin allow-forms allow-popups allow-modals'
          }
          allow="camera; microphone; geolocation; midi; encrypted-media;"
          loading="lazy"
        />
      )}
    </div>
  );
};
